import React, { useEffect, useState } from 'react';
import { getAllTabless, putTableOccupied, putTableFree } from '../services/api';
import { usePOS } from '../context/POSContext';
import Receipt from './Receipt';

const TableView = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [showBillModal, setShowBillModal] = useState(false);
  const [billsForTable, setBillsForTable] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);

  const {
    setActiveTable,
    getTableHistory,
    fetchLast10BillsForTable,
  } = usePOS();

  useEffect(() => {
    fetchTables();
  }, [page]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const res = await getAllTabless(page, size);
      const content = res.data?.content || [];
      setTables(Array.isArray(content) ? content : []);
      setTotalPages(res.data?.totalPages || 1);
    } catch (err) {
      console.error('Erreur lors du chargement des tables', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetOccupied = async (id) => {
    try {
      await putTableOccupied(id);
      fetchTables();
    } catch (err) {
      console.error('Erreur en occupant la table', err);
    }
  };

  const handleSetFree = async (id) => {
    try {
      await putTableFree(id);
      fetchTables();
    } catch (err) {
      console.error('Erreur en libérant la table', err);
    }
  };

  const handleSelectTable = (table) => {
    setActiveTable(table);
  };

  const handleShowLast10Bills = async (tableId) => {
    try {
      const bills = await fetchLast10BillsForTable(tableId);
      setBillsForTable(bills);
      setShowBillModal(true);
      setSelectedBill(null);
    } catch (err) {
      console.error('Erreur lors de la récupération des dernières factures', err);
      alert('Impossible de charger les dernières factures.');
    }
  };

  const handleShowBillDetail = (bill) => {
    setSelectedBill(bill);
  };

  const handlePrintBill = () => {
    window.print();
  };

  const closeBillModal = () => {
    setShowBillModal(false);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .printable-receipt, .printable-receipt * {
              visibility: visible;
            }
            .printable-receipt {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
              font-size: 12pt;
              background: white;
              border: 1px solid #000;
            }
            .print-hidden {
              display: none !important;
            }
            .modal-container {
              background: none !important;
              display: block !important;
            }
            .modal-content {
              box-shadow: none !important;
              border: none !important;
              padding: 0 !important;
            }
            .text-center {
              text-align: center !important;
            }
            .flex {
              display: flex !important;
            }
            .justify-between {
              justify-content: space-between !important;
            }
            .mb-4 {
              margin-bottom: 1rem !important;
            }
            .py-3 {
              padding-top: 0.75rem !important;
              padding-bottom: 0.75rem !important;
            }
            .text-sm {
              font-size: 10pt !important;
            }
            .font-bold {
              font-weight: bold !important;
            }
            .w-10 {
              width: 40px !important;
            }
            .w-16 {
              width: 64px !important;
            }
          }
        `}
      </style>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-6 text-gray-800">Liste des Tables</h1>

          {loading ? (
            <p className="text-gray-500 text-center animate-pulse">Chargement...</p>
          ) : tables.length === 0 ? (
            <p className="text-gray-500 text-center">Aucune table disponible.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {tables.map((table) => (
                <div
                  key={table.id}
                  className={`border border-gray-200 rounded-lg p-3 shadow-sm bg-white ${
                    table.occupied ? 'bg-red-50' : 'bg-green-50'
                  } transition-all hover:shadow-md hover:scale-105 transform`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`flex items-center text-sm font-medium px-2 py-1 rounded ${
                        table.occupied ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
                      }`}
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="10" cy="10" r="5" />
                      </svg>
                      {table.occupied ? 'Occupée' : 'Libre'}
                    </span>
                  </div>
                  <h2 className="font-semibold text-xl text-gray-800">
                    Table {table.tableNumber}
                  </h2>
                  <p className="text-sm text-gray-600">Places : {table.guestsNumber}</p>

                  {table.lastReceipt && (
                    <div className="mt-2 text-sm bg-gray-100 p-2 rounded grid grid-cols-2 gap-2">
                      <div>
                        <p className="font-semibold text-gray-700">Dernière Facture :</p>
                        <p className="text-gray-600">
                          Total : {table.lastReceipt.total?.toFixed(2)} €
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-700">Date :</p>
                        <p className="text-gray-600">
                          {new Date(table.lastReceipt.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-3 space-y-1.5">
                    <button
                      className="flex items-center justify-center bg-blue-500 text-white px-3 py-1.5 rounded w-full hover:bg-blue-600 transition-colors"
                      onClick={() => handleSelectTable(table)}
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5v-4a2 2 0 012-2h10a2 2 0 012 2v4h-4m-6 0h.01"
                        />
                      </svg>
                      Sélectionner
                    </button>
                    <button
                      className="flex items-center justify-center bg-purple-500 text-white px-3 py-1.5 rounded w-full hover:bg-purple-600 transition-colors"
                      onClick={() => handleShowLast10Bills(table.id)}
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                      Voir les 10 dernières factures
                    </button>
                    {table.occupied ? (
                      <button
                        className="flex items-center justify-center bg-green-500 text-white px-3 py-1.5 rounded w-full hover:bg-green-600 transition-colors"
                        onClick={() => handleSetFree(table.id)}
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                          />
                        </svg>
                        Libérer
                      </button>
                    ) : (
                      <button
                        className="flex items-center justify-center bg-red-500 text-white px-3 py-1.5 rounded w-full hover:bg-red-600 transition-colors"
                        onClick={() => handleSetOccupied(table.id)}
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 11c0-1.1.9-2 2-2s2 .9 2 2-2 4-2 4m0 0c-1.1 0-2-.9-2-2s.9-2 2-2zm0 0V7m0 4v4m-8 4h16a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        Occuper
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showBillModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 modal-container">
              <div className="bg-white p-6 rounded-xl max-w-xl w-full border-t-4 border-amber-600 modal-content printable-receipt-container flex flex-col" style={{ maxHeight: '90vh' }}>
                <h2 className="text-xl font-bold mb-4 text-gray-800 print-hidden">10 Dernières Factures</h2>
                <div className="mb-4 print-hidden overflow-y-auto" style={{ maxHeight: '250px' }}>
                  {billsForTable.length === 0 ? (
                    <p className="text-gray-500 text-center">Aucune facture trouvée.</p>
                  ) : (
                    <ul className="divide-y divide-gray-200">
                      {billsForTable.map((bill, idx) => (
                        <li key={bill.id} className="py-2 flex items-center justify-between">
                          <div>
                            <span className="font-semibold">{idx + 1}.</span>{' '}
                            <span>Total : {bill.finalAmmount?.toFixed(2)} MAD</span>{' '}
                            <span className="text-xs text-gray-500 ml-2">{new Date(bill.createdAt).toLocaleString()}</span>
                          </div>
                          <button
                            className={`ml-4 px-2 py-1 rounded text-black ${selectedBill && selectedBill.id === bill.id ? 'bg-amber-600 text-white' : 'bg-amber-400 hover:bg-amber-500'}`}
                            onClick={() => handleShowBillDetail(bill)}
                          >
                            Voir
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto mb-4" style={{ maxHeight: '300px' }}>
                  {selectedBill && (
                    <Receipt bill={selectedBill} showActions={false} />
                  )}
                  </div>
               
                <div className="mt-4 flex gap-2 print-hidden">
                  <button
                    className="flex items-center justify-center bg-gray-300 text-black px-4 py-2 rounded flex-1 hover:bg-gray-400 transition-colors"
                    onClick={closeBillModal}
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Fermer
                  </button>
               
                  <button
                    className="flex items-center justify-center 
                    bg-amber-600 text-white px-4 py-2 rounded flex-1 
                    hover:bg-amber-700 transition-colors"
                    onClick={handlePrintBill}
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 
                        4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Imprimer
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-center items-center gap-4">
            <button
              disabled={page === 0}
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              className="flex items-center bg-gray-300 px-5 py-2.5 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Précédent
            </button>
            <span className="text-gray-700 bg-amber-100 px-3 py-1 rounded-full">
              Page {page + 1} / {totalPages}
            </span>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((prev) => prev + 1)}
              className="flex items-center bg-gray-300 px-5 py-2.5 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
            >
              Suivant
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TableView;